import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Alert } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useLogin } from "../../hooks/useAuth";
import { useAuthStore } from "../../stores/authStore";
import "./SignIn.css";

const SignIn = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, company } = useAuthStore();
  const loginMutation = useLogin();
  const [form] = Form.useForm();

  // Determine if company is SBY (Sealanie Boat Yard) for styling
  // After login, use company from JWT token, before login use environment variable
  const isSBY = isAuthenticated
    ? company === "sby"
    : (process.env.REACT_APP_COMPANY_NAME?.includes("Sealanie") ||
       process.env.REACT_APP_COMPANY_NAME?.includes("Boat"));

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const onFinish = async (values) => {
    try {
      await loginMutation.mutateAsync({
        username: values.username,
        password: values.password,
      });
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  return (
    <div className="signin-page">
      {/* Left branded panel — hidden on mobile */}
      {/* Only show brand background for SBY, RBS uses white background */}
      <div
        className="signin-brand-panel"
        style={isSBY && process.env.REACT_APP_BRAND_COLOR ? { background: process.env.REACT_APP_BRAND_COLOR } : undefined}
      >
        <div className="signin-brand-content">
          <img
            src={process.env.REACT_APP_LOGO_URL}
            alt="Logo"
            className="signin-brand-logo"
            style={process.env.REACT_APP_LOGO_FULL_COLOR === "true" ? { filter: "none", opacity: 1 } : undefined}
          />
          <h2 className="signin-brand-title">{process.env.REACT_APP_COMPANY_NAME}</h2>
          <p className="signin-brand-subtitle">Integrated Business Platform</p>
        </div>
        <span className="signin-brand-footer">
          &copy; {new Date().getFullYear()} {process.env.REACT_APP_COMPANY_NAME_FULL}
        </span>
      </div>

      {/* Right form panel */}
      <div className="signin-form-panel">
        <div className="signin-form-wrapper">
          {/* Mobile-only logo */}
          <div className="signin-mobile-logo">
            <img
              src={process.env.REACT_APP_LOGO_URL}
              alt="Logo"
            />
          </div>

          <h1 className="signin-heading">Welcome back</h1>
          <p className="signin-subheading">Sign in to continue to your account</p>

          {loginMutation.error && (
            <Alert
              message="Invalid username or password"
              type="error"
              showIcon
              className="signin-alert"
            />
          )}

          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            className="signin-form"
          >
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: "Please enter your username" }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Enter username"
                size="large"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: "Please enter your password" }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter password"
                size="large"
              />
            </Form.Item>

            <Form.Item className="signin-submit-item">
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loginMutation.isLoading || isLoading}
                style={process.env.REACT_APP_BRAND_COLOR ? { backgroundColor: process.env.REACT_APP_BRAND_COLOR, borderColor: process.env.REACT_APP_BRAND_COLOR } : undefined}
              >
                Sign In
              </Button>
            </Form.Item>

            <div className="signin-forgot">
              <Button
                type="link"
                onClick={() => navigate("/password-reset-request")}
                className="signin-forgot-btn"
                style={process.env.REACT_APP_BRAND_COLOR ? { color: process.env.REACT_APP_BRAND_COLOR } : undefined}
              >
                Forgot password?
              </Button>
            </div>
          </Form>

          <div className="signin-powered">
            powered by{" "}
            <a
              href="https://autone.lk"
              target="_blank"
              rel="noopener noreferrer"
              style={process.env.REACT_APP_BRAND_COLOR ? { color: process.env.REACT_APP_BRAND_COLOR } : undefined}
            >
              autone.lk
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
